<template>
    <div class="container mx-auto px-8 grid grid-cols-5 gap-4">
        <div class="col-span-5">
            <HeaderComponent :title="`Edit Affiliate: ${affiliate.name}`" :actions="actions"/>
        </div>
        <div class="col-span-1 flex flex-col gap-4" v-if="affiliate.parent_id === null">
            <h3 class="text-lg font-bold">Add Input Types</h3>
            <div v-for="(type, name) in inputTypes" :key="name" class="bg-gray-200 hover:bg-gray-300 cursor-pointer p-4 rounded-sm h-8 flex items-center justify-center" @click="addInputType(type)">
                <div class="text-sm font-bold">{{ name }}</div>
            </div>
        </div>
        <div class="col-span-2"  v-if="affiliate.parent_id === null">
            <JsonEditorVue
                v-model="schema"
                class="w-full h-full"
            ></JsonEditorVue>
        </div>
        <div class="col-span-2 relative p-8 border border-gray-200 rounded-sm"  v-if="affiliate.parent_id === null">
            <FormKit
                @submit="fakeSubmit"
                type="form"
                id="editor-form"
                class="relative z-20"
            >
                <FormKitSchema :schema="schema" />
            </FormKit>
        </div>
        <div class="col-span-5">
            <h3 class="text-lg font-bold">Rules</h3>
            <FormKit
                type="form"
                id="rules-form"
                class="relative z-20"
                :actions="false"
            >
                <FormKit
                    v-if="affiliate.parent_id === null"
                    type="checkbox"
                    id="auto_signup"
                    v-model="affiliate.auto_signup"
                    label="Auto Signup (Auto-signup affiliates must have an affiliate_name and email field. These must be named 'affiliate_name' and 'email'. A password input type is recomended otherwise a random password will be used.)"
                ></FormKit>
                <FormKit
                    type="text"
                    id="affiliate_name"
                    v-model="affiliate.name"
                    label="Affiliate Name"
                ></FormKit>
                <FormKit
                    type="select"
                    id="affiliate_owner"
                    v-model="affiliate.owner_id"
                    label="Affiliate Owner"
                    :options="users"
                ></FormKit>
                <FormKit
                    type="select"
                    id="affiliate_type"
                    v-model="affiliate.affiliate_type_id"
                    label="Affiliate Type"
                    :options="types"
                ></FormKit>
            </FormKit>
        </div>
    </div>
</template>

<script setup>
import { FormKit, FormKitSchema, reset } from "@formkit/vue";
import { usePage, router } from "@inertiajs/vue3";
import { computed } from "vue";
import { toast } from "vue3-toastify";
import JsonEditorVue from "json-editor-vue";
import HeaderComponent from "@/Components/HeaderComponent.vue";

const page = usePage();
const affiliate = computed(() => page.props.affiliate);
const authUser = computed(() => page.props.authUser);
const users = computed(() => page.props.users);
const types = computed(() => page.props.types);

const actions = computed(() => {
    let actions = [
        {label: 'View Form', click: () => router.get(`/${affiliate.value.short_uuid}`), class: 'bg-blue-500 text-white'}, 
        {label: 'View Submissions', click: () => router.get(`/affiliates/${affiliate.value.short_uuid}/submissions/`), class: 'bg-blue-500 text-white'},
    ];
    if (authUser.value.is_admin && affiliate.value.parent_id === null) {
        actions.push({label: 'Edit Signup Form', click: () => router.get(`/affiliates/${affiliate.value.short_uuid}/edit-signup`), class: `bg-blue-500 text-white`});
    }
    actions.push({label: 'Save', click: () => submit(), class: 'bg-green-500 text-white'});

    return actions;
})

const inputTypes = {
    text: {
        $formkit: "text",
        label: "Text",
        name: "text",
        validation: "required",
    },
    img: {
        $el: "img",
        attrs: {
            src: "https://via.placeholder.com/150",
        },
    },
    email: {
        $formkit: "email",
        label: "Email",
        name: "email",
        validation: "required|email",
    },
    checkbox: {
        $formkit: "checkbox",
        label: "Checkbox",
        name: "checkbox",
        validation: "required",
    },
    tel: {
        $formkit: "tel",
        name: "tel",
        label: "*Telephone",
        placeholder: "xxx-xxx-xxxx",
        help: "Phone number must be in the xxx-xxx-xxxx format.",
        validation: "required|matches:/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/",
    },
    date: {
        $formkit: "date",
        label: "Date",
        name: "date",
        validation: "required",
    },
    textarea: {
        $formkit: "textarea",
        label: "Description",
        name: "description",
        help: "Must be between 20 and 500 characters.",
        placeholder: "Enter your description here.",
        validation: "required|length:20,500",
    },
    password: {
        $formkit: "password",
        label: "Password",
        name: "password",
        validation: "required",
    },
};

const addInputType = (type) => {
    schema.value = [...schema.value, type];
};

const schema = computed({
    get: () => JSON.parse(affiliate.value.formkit_schema) ?? [],
    set: (value) => {
        let tempVal;
        try {
            tempVal = typeof value === "object" ? JSON.stringify(value) : value;
            JSON.parse(tempVal); // Check if tempVal is valid JSON
        } catch (e) {
            return;
            // do nothing. probably not valid json yet
        }
        affiliate.value.formkit_schema = tempVal;
    },
});

const fakeSubmit = () => {
    toast("Submitted", {
        type: "success",
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 1000,
    });
    reset("editor-form");
};

const clearSchema = () => {
    if (
        !confirm(
            "Are you sure you want to clear the schema? This action cannot be undone.",
        )
    ) {
        return;
    }
    router.post(
        `/affiliates/${affiliate.value.short_uuid}/clear-schema/`,
        {},
        {
            headers: {
                "X-CSRF-Token": usePage().props.csrfToken,
            },
        },
    );
};

const submit = () => {
    router.post(window.location.href, page.props.affiliate, {
        headers: {
            "X-CSRF-Token": usePage().props.csrfToken,
        },
    });
};
</script>
